// Agregasi PURE halaman Detail Pre-order (/admin/orders/po/[id]) — dipisah
// dari board.ts (yang ber-"use client") supaya bisa dipakai juga oleh route
// SERVER (mis. /api/admin/po-reseller-export yang membangun file Excel rekap
// per pelanggan). board.ts me-re-export seluruh isi modul ini sehingga semua
// importer lama (`@/lib/board`) tetap bekerja tanpa perubahan.
//
// JANGAN menambahkan import bergantung-browser (createBrowserSupabase dkk) ke
// file ini — fetcher-nya tetap tinggal di board.ts.

// Umur relatif berbahasa Indonesia ("Baru saja" / "2 jam lalu" / tanggal) —
// dipakai-ulang, bukan disalin, supaya "2 jam lalu" di kartu konfirmasi berarti
// persis sama dengan "2 jam lalu" di notifikasi admin. `adminNotifications.ts`
// murni (tanpa "use client"), jadi modul ini tetap aman diimpor route server.
import { formatNotifAge } from "@/lib/adminNotifications";
// Peta slot → kolom diskon: SATU sumber (mig 5600). reseller-groups.ts murni
// tanpa import, jadi aman bagi modul yang juga dipakai route SERVER.
import { DISKON_SLOTS, DISKON_SLOT_COLUMN } from "@/lib/reseller-groups";
// Bahan harga pesan konfirmasi WA. Keduanya murni (tanpa "use client"), jadi
// modul ini tetap aman diimpor route server.
import {
  variantPricingFromRow,
  type VariantPricing
} from "@/lib/backfillUnitPrice";
import {
  discountTraceFromRow,
  type DiscountTraceRow
} from "@/lib/cartItemPrice";

/** Catatan satu pembeli untuk sebuah varian. */
export type VariantNoteEntry = {
  buyerName: string;
  note: string;
};

// ─── Detail Pre-order (halaman /admin/orders/po/[id]) ─────────────────────────

/** Rekap satu varian: total qty + jumlah reseller + catatan tiap pembeli. */
export type PODetailVariant = {
  variantId: string | null;
  variantName: string;
  totalQty: number;
  /** Jumlah pembeli unik yang memesan varian ini. */
  resellerCount: number;
  /** Nama + qty per pembeli yang memesan varian ini, urut nama. */
  buyers: { buyerName: string; qty: number }[];
  notes: VariantNoteEntry[];
};

/** Satu baris pesanan milik seorang pembeli pada rekap per-pelanggan. */
export type PODetailResellerItem = {
  variantName: string;
  qty: number;
  note: string | null;
};

/**
 * Satu baris UANG pesanan seorang pembeli — bahan harga di pesan konfirmasi WA
 * (`buildPOCustomerConfirmText`). Sengaja TERPISAH dari `items`: kuncinya beda.
 * `items` digabung per varian + catatan (yang dilihat admin di kartu), baris ini
 * digabung per varian + HARGA — dua baris varian sama berharga beda (checkout
 * ganda di dua waktu, snapshot lama) tak boleh dicetak sebagai satu harga.
 */
export type PODetailPriceLine = {
  variantId: string | null;
  variantName: string;
  qty: number;
  /**
   * Snapshot `order_items.unit_price` = harga yang DITAGIH per pcs. `null` =
   * kosong (produk "harga menyusul") ATAU kolomnya memang tak diminta pemanggil
   * (route export Excel) — dua-duanya berarti tak ada harga untuk dicetak.
   */
  unitPrice: number | null;
  /** `order_items.manual_price_normal` (Harga baris produk Non Web, mig 6390). */
  manualPriceNormal: number | null;
  /** Potret tambahan baris (`order_items.unit_price_extra`, mig 7900); null = kolom tak diminta / pra-7900 = 0. */
  priceExtra: number | null;
  /** Baris ber-voucher: Rp 0 di baris ini SAH, bukan "harga menyusul". */
  hasVoucher: boolean;
  /** Jejak potongan saat memesan (promo/level); null = tak tercatat. */
  trace: DiscountTraceRow | null;
};

/**
 * Rekap satu pembeli: daftar varian + qty + catatan. Nilai uang HANYA ada di
 * `priceLines` (bahan pesan konfirmasi WA) — kartu di layar tak mencetaknya.
 */
export type PODetailReseller = {
  customerId: string;
  /** Order id pembeli untuk produk ini (dipakai admin saat edit pesanan). */
  orderId: string;
  customerName: string;
  phone: string;
  /** Nama admin handler (customers.assigned_admin_id → profiles). */
  handlerName: string | null;
  /**
   * Id admin handler mentah — dipakai filter "Orderan Saya / Semua Orderan"
   * lewat `inAdminScope`. SENGAJA terpisah dari `handlerName`: dua admin bisa
   * bernama sama, jadi menyaring pakai nama akan salah orang.
   */
  assignedAdminId: string | null;
  /**
   * Stempel "sudah dikonfirmasi via WhatsApp" (mig 3580) — null = belum pernah.
   * Diambil yang TERBARU di antara semua order pembeli ini untuk produk tsb:
   * satu pembeli bisa punya >1 order (checkout ganda) yang oleh kartu ini
   * memang sudah digabung jadi satu baris, jadi kartunya juga harus punya satu
   * jawaban — bukan "sudah" atau "belum" tergantung order mana yang terbaca.
   */
  confirmedAt: string | null;
  /** Nama admin penanda; null bila profilnya sudah dihapus (FK SET NULL). */
  confirmedByName: string | null;
  /**
   * `created_at` baris pesanan AKTIF terbaru milik pembeli ini, lintas order —
   * bahan `poConfirmChangedSince` ("berubah sejak dikonfirmasi"). Absen/null =
   * kolomnya tak diminta pemanggil (mis. ekspor Excel), bukan "tak berubah".
   */
  lastLineAt?: string | null;
  /**
   * Level pelanggan sekarang (`customers.group_id`); null = Customer biasa.
   * Dipakai membaca setelan diskon varian untuk level ini di pesan konfirmasi.
   */
  groupId: string | null;
  totalQty: number;
  items: PODetailResellerItem[];
  /** Baris uang pesanan pembeli ini — lihat `PODetailPriceLine`. */
  priceLines: PODetailPriceLine[];
};

/**
 * Diskon PRODUK per slot grup reseller — override `product_variants.diskon_*`
 * (slot 1=Reseller, 2=Agen, 3=slot3 … 15=slot15). Dipakai baris "Diskon" broadcast
 * WA/PDF agar mengikuti setelan tiap PRODUK (bukan diskon grup reseller global).
 * Hanya slot ber-override (>0) yang disertakan; slot lain fallback ke diskon grup.
 */
export type PODiscountOverride = {
  slot: number;
  mode: "persen" | "nominal";
  value: number;
};

/** Data lengkap halaman Detail Pre-order untuk satu produk. */
export type PODetailData = {
  productId: string;
  productName: string;
  brand: string;
  status: string;
  /** preorder_deadline (rencana tutup PO). */
  deadline: string | null;
  /** closed_at — null selama PO masih OPEN. */
  closedAt: string | null;
  estimatedReady: string | null;
  totalPcs: number;
  /** Jumlah pembeli/reseller unik. */
  totalReseller: number;
  /**
   * Σ qty × unit_price (snapshot harga saat order; null → 0).
   *
   * **`null` = angka uang SENGAJA tidak diambil** karena pemirsanya bukan super
   * admin (opsi `withMoney`, lihat `fetchPODetailFromSupabase`) — BUKAN "nol
   * transaksi". Sama seperti `estimasiProfit`: konsumen wajib `!== null`,
   * JANGAN truthiness — PO yang belum punya harga sah bernilai 0.
   */
  totalTransaksi: number | null;
  /**
   * Σ qty × (unit_price − hpp). HPP nullable → 0, jadi ini estimasi kasar.
   *
   * **`null` = angka modal SENGAJA tidak diambil** karena pemirsanya bukan
   * super admin (opsi `withMoney`, lihat `fetchPODetailFromSupabase`) — BUKAN
   * "profitnya nol". Nullable-nya disengaja sebagai pagar tipe: kalau field ini
   * tetap `number`, jalur yang tak meminta kolom `hpp` akan menghitung
   * Σ qty × unit_price (= OMZET, karena hpp jatuh ke 0) lalu merendernya
   * sebagai "profit" — salah dan senyap. Konsumen wajib memeriksa `!== null`,
   * JANGAN truthiness: profit 0 itu nilai yang sah.
   */
  estimasiProfit: number | null;
  variants: PODetailVariant[];
  resellers: PODetailReseller[];
  /** Diskon yang MELEKAT pada produk (override varian) per slot grup reseller. */
  productDiscounts: PODiscountOverride[];
  /**
   * Harga jual umum + setelan diskon SELURUH varian (termasuk yang nonaktif,
   * karena pesanan lama bisa menunjuk varian yang kini dimatikan) — bahan
   * harga per varian di pesan konfirmasi WA. Bentuknya yang dipahami pricing.ts.
   */
  variantPricing: VariantPricing[];
};

// Bentuk baris mentah dari Supabase (sebelum diagregasi).
export type PODetailHeaderRow = {
  id: string;
  name: string;
  brand: string | null;
  status: string;
  preorder_deadline: string | null;
  closed_at: string | null;
  estimated_ready: string | null;
  product_variants:
    | {
        id: string;
        name: string;
        sort_order: number | null;
        hpp: number | null;
        /** Absen bila pemanggil tak memintanya (route export Excel). */
        price_normal?: number | string | null;
        /** mig 7900: tambahan per varian; absen = 0. */
        price_extra?: number | string | null;
        diskon_mode: string | null;
        diskon_reseller: number | null;
        diskon_agen: number | null;
        diskon_slot3: number | null;
        diskon_slot4: number | null;
        diskon_slot5: number | null;
        diskon_slot6: number | null;
        diskon_slot7: number | null;
        diskon_slot8: number | null;
        diskon_slot9: number | null;
        diskon_slot10: number | null;
        diskon_slot11: number | null;
        diskon_slot12: number | null;
        diskon_slot13: number | null;
        diskon_slot14: number | null;
        diskon_slot15: number | null;
      }[]
    | null;
};

export type PODetailOrderRow = {
  id: string;
  customer_id: string;
  customer_name: string | null;
  phone: string | null;
  /** Embed customers → profiles (nama admin handler). Bisa objek atau array. */
  customers?:
    | {
        assigned_admin_id?: string | null;
        group_id?: string | null;
        profiles?: { full_name: string | null } | null;
      }
    | {
        assigned_admin_id?: string | null;
        group_id?: string | null;
        profiles?: { full_name: string | null } | null;
      }[]
    | null;
  /** Stempel konfirmasi WA (mig 3580) + embed nama admin penandanya. */
  po_confirmed_at?: string | null;
  po_confirmed_profile?:
    | { full_name: string | null }
    | { full_name: string | null }[]
    | null;
  order_items:
    | {
        variant_id: string | null;
        variant_name: string;
        qty: number | null;
        /** Kolom uang di bawah ini absen bila pemanggil tak memintanya. */
        unit_price?: number | string | null;
        unit_price_extra?: number | string | null;
        manual_price_normal?: number | string | null;
        voucher_name?: string | null;
        promo_discount_mode?: string | null;
        promo_discount_value?: number | string | null;
        level_discount_mode?: string | null;
        level_discount_value?: number | string | null;
        item_note: string | null;
        keep_status: string | null;
        /** Absen bila pemanggil tak memintanya — lihat `PODetailReseller.lastLineAt`. */
        created_at?: string | null;
      }[]
    | null;
};

/** Angka dari kolom numeric (PostgREST bisa mengirim string); tak sah → null. */
function numOrNull(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

// ─── Smart variant sorting ────────────────────────────────────────────────────

/**
 * Urutan standar ukuran pakaian (case-insensitive). Alias numerik (2XL, 3XL …)
 * dipetakan ke posisi yang sama dengan bentuk X-berulang.
 */
const SIZE_RANK = new Map<string, number>(
  (["XXXS", "XXS", "XS", "S", "M", "L", "XL", "XXL", "XXXL", "XXXXL"] as const)
    .map((s, i) => [s, i] as [string, number])
);
// Alias: 2XL = XXL, 3XL = XXXL, 4XL = XXXXL, 5XL+
["2XL", "3XL", "4XL", "5XL", "6XL", "7XL"].forEach((alias, i) => {
  const base = ["XXL", "XXXL", "XXXXL"][i] ?? null;
  SIZE_RANK.set(alias, base ? (SIZE_RANK.get(base) ?? 10 + i) : 10 + i);
});

/** Bandingkan satu segmen nama varian: ukuran → angka → teks A-Z. */
function smartPartCompare(a: string, b: string): number {
  const au = a.trim().toUpperCase();
  const bu = b.trim().toUpperCase();
  const ra = SIZE_RANK.get(au);
  const rb = SIZE_RANK.get(bu);
  if (ra !== undefined && rb !== undefined) return ra - rb;
  if (ra !== undefined) return -1;
  if (rb !== undefined) return 1;
  const na = Number(a.trim());
  const nb = Number(b.trim());
  if (!isNaN(na) && !isNaN(nb)) return na - nb;
  if (!isNaN(na)) return -1;
  if (!isNaN(nb)) return 1;
  return a.trim().localeCompare(b.trim(), "id");
}

/**
 * Bandingkan dua nama varian secara cerdas: tiap segmen (dipisah " / ")
 * dibandingkan secara ukuran → angka → A-Z.
 */
export function smartVariantNameCompare(a: string, b: string): number {
  const pa = a.split(" / ").map((p) => p.trim()).filter(Boolean);
  const pb = b.split(" / ").map((p) => p.trim()).filter(Boolean);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const cmp = smartPartCompare(pa[i] ?? "", pb[i] ?? "");
    if (cmp !== 0) return cmp;
  }
  return 0;
}

/**
 * Field diskon per-slot pada `product_variants` (slot 1=Reseller … 15=slot15).
 * Diturunkan dari `DISKON_SLOT_COLUMN` (mig 5600), bukan diketik ulang: daftar
 * yang tertinggal satu slot membuat broadcast WA/PDF menyebut diskon berbeda
 * dari yang benar-benar ditagihkan, tanpa satu pun error.
 */
const PO_DISCOUNT_SLOT_FIELDS: readonly (readonly [number, string])[] =
  DISKON_SLOTS.map((slot) => [slot, DISKON_SLOT_COLUMN[slot]] as const);

/**
 * Diskon yang MELEKAT pada produk (untuk baris "Diskon" broadcast WA/PDF): baca
 * override `product_variants.diskon_*` per slot grup reseller. ProductForm
 * menulis "Pengaturan Diskon" secara seragam ke semua varian, jadi cukup ambil
 * nilai non-nol pertama (urut sort_order) tiap slot beserta modenya (persen/
 * nominal). Slot tanpa override tak disertakan → pemanggil fallback ke diskon grup.
 */
export function deriveProductDiscounts(
  variants: ({
    sort_order?: number | null;
    diskon_mode?: string | null;
  } & Partial<Record<string, number | string | null>>)[]
): PODiscountOverride[] {
  const sorted = [...(variants ?? [])].sort(
    (a, b) =>
      (a.sort_order ?? Number.MAX_SAFE_INTEGER) -
      (b.sort_order ?? Number.MAX_SAFE_INTEGER)
  );
  const result: PODiscountOverride[] = [];
  for (const [slot, field] of PO_DISCOUNT_SLOT_FIELDS) {
    for (const v of sorted) {
      const value = Number(v[field] ?? 0);
      if (Number.isFinite(value) && value > 0) {
        result.push({
          slot,
          mode: v.diskon_mode === "nominal" ? "nominal" : "persen",
          value
        });
        break;
      }
    }
  }
  return result;
}

/**
 * Agregasi murni (tanpa I/O) untuk halaman Detail Pre-order: dari header produk +
 * daftar order, hasilkan total, rekap per-varian (flat, beserta catatan), dan
 * rekap per-pelanggan. Dipisah agar bisa diuji unit tanpa mock Supabase.
 *
 * `opts.withMoney` **default `false` (fail-closed)** = "pemirsanya super admin":
 * menyalakan DUA angka uang kartu ringkasan sekaligus — **Total Transaksi**
 * (Σ qty × unit_price) dan **Estimasi Profit** (Σ qty × (harga − hpp)).
 *
 * SATU flag untuk keduanya, bukan dua, karena gerbangnya memang satu hal yang
 * sama (`profiles.is_super_admin`). Dua flag hanya mengundang penyimpangan:
 * suatu hari satu layar mengirim yang satu tanpa yang lain, dan salah satu
 * angka bocor tanpa ada yang menyadari.
 *
 * Default `true` akan membuat pemanggil baru yang lupa mengoper flag
 * memancarkan omzet berlabel profit; default `false` paling buruk
 * menyembunyikan angka yang memang boleh tampil — arah salah yang bisa
 * diterima untuk angka uang.
 *
 * `unit_price` per baris TIDAK ikut digerbang flag ini (sejak 2026-09-29):
 * harga tiap pembeli dicetak di pesan konfirmasi WA yang dikirim admin handler
 * (`PODetailReseller.priceLines`). Yang tetap tertutup adalah RINGKASAN
 * satu-angka-seluruh-PO di atas — kolomnya boleh ada, angkanya tetap `null`.
 */
export function aggregatePODetail(
  header: PODetailHeaderRow,
  orders: PODetailOrderRow[],
  opts?: { withMoney?: boolean }
): PODetailData {
  // Meta varian (hpp + urutan tampil): index by id, fallback by name.
  const metaById = new Map<string, { hpp: number; sortOrder: number }>();
  const metaByName = new Map<string, { hpp: number; sortOrder: number }>();
  for (const v of header.product_variants ?? []) {
    const meta = {
      hpp: v.hpp ?? 0,
      sortOrder: v.sort_order ?? Number.MAX_SAFE_INTEGER
    };
    metaById.set(v.id, meta);
    metaByName.set(v.name, meta);
  }

  let totalPcs = 0;
  let totalTransaksi = 0;
  let estimasiProfit = 0;
  const resellerIds = new Set<string>();

  type VariantAgg = {
    variantId: string | null;
    variantName: string;
    totalQty: number;
    buyerMap: Map<string, { buyerName: string; qty: number }>; // keyed by customerId
    notes: VariantNoteEntry[];
    sortOrder: number;
  };
  const variantMap = new Map<string, VariantAgg>(); // key: variant_name
  const resellerMap = new Map<string, PODetailReseller>();
  // Indeks baris uang per pembeli — kunci lihat `priceLineKey`.
  const priceLineIndex = new Map<string, PODetailPriceLine>();

  for (const order of orders ?? []) {
    const cid = order.customer_id;
    const buyerName = order.customer_name ?? "—";
    if (cid) resellerIds.add(cid);

    let reseller = resellerMap.get(cid);
    if (!reseller) {
      const custRel = Array.isArray(order.customers)
        ? order.customers[0]
        : order.customers;
      reseller = {
        customerId: cid,
        orderId: order.id,
        customerName: buyerName,
        phone: order.phone ?? "",
        handlerName: custRel?.profiles?.full_name ?? null,
        assignedAdminId: custRel?.assigned_admin_id ?? null,
        confirmedAt: null,
        confirmedByName: null,
        lastLineAt: null,
        groupId: custRel?.group_id ?? null,
        totalQty: 0,
        items: [],
        priceLines: []
      };
      resellerMap.set(cid, reseller);
    }

    // Stempel konfirmasi WA (mig 3580) terbaru lintas order milik pembeli ini.
    mergePoConfirmStamp(reseller, order);

    for (const item of order.order_items ?? []) {
      if (item.keep_status === "cancelled") continue;
      // Baris aktif terbaru pembeli ini — bahan "berubah sejak dikonfirmasi".
      // Dibandingkan sebagai WAKTU (offset zona bisa beda), bukan string.
      const lineAt = item.created_at ?? null;
      if (
        lineAt &&
        !Number.isNaN(Date.parse(lineAt)) &&
        (!reseller.lastLineAt || Date.parse(lineAt) > Date.parse(reseller.lastLineAt))
      ) {
        reseller.lastLineAt = lineAt;
      }
      const qty = item.qty ?? 0;
      const unitPrice = numOrNull(item.unit_price) ?? 0;
      const note = (item.item_note ?? "").trim();
      const meta =
        (item.variant_id ? metaById.get(item.variant_id) : undefined) ??
        metaByName.get(item.variant_name);
      const hpp = meta?.hpp ?? 0;
      const sortOrder = meta?.sortOrder ?? Number.MAX_SAFE_INTEGER;

      totalPcs += qty;
      totalTransaksi += qty * unitPrice;
      estimasiProfit += qty * (unitPrice - hpp);

      // Rekap per-varian (key by variant_name agar sejalan dgn grouping catatan).
      let vagg = variantMap.get(item.variant_name);
      if (!vagg) {
        vagg = {
          variantId: item.variant_id ?? null,
          variantName: item.variant_name,
          totalQty: 0,
          buyerMap: new Map<string, { buyerName: string; qty: number }>(),
          notes: [],
          sortOrder
        };
        variantMap.set(item.variant_name, vagg);
      }
      vagg.totalQty += qty;
      if (cid) {
        const prev = vagg.buyerMap.get(cid);
        if (prev) {
          prev.qty += qty;
        } else {
          vagg.buyerMap.set(cid, { buyerName, qty });
        }
      }
      if (note) vagg.notes.push({ buyerName, note });

      // Rekap per-pelanggan — gabung per varian + CATATAN (qty dijumlah) supaya
      // tidak ada baris ganda saat satu pembeli punya >1 order aktif / beberapa
      // item untuk varian yang sama (mis. checkout ganda), TAPI catatan yang
      // berbeda tetap jadi barisnya sendiri.
      //
      // Catatan ikut jadi kunci karena ia bukan hiasan: admin memakainya untuk
      // menandai penerima tiap pcs ("Supi", "Rina"). Versi lama menggabung per
      // nama varian saja dan menyimpan catatan yang PERTAMA ada — sisanya tak
      // pernah sampai ke layar, rekap WA, maupun Excel.
      reseller.totalQty += qty;
      const existingItem = reseller.items.find(
        (x) => x.variantName === item.variant_name && (x.note ?? "") === note
      );
      if (existingItem) {
        existingItem.qty += qty;
      } else {
        reseller.items.push({
          variantName: item.variant_name,
          qty,
          note: note || null
        });
      }

      // Baris uang: gabung per varian + HARGA, catatan diabaikan (pesan
      // konfirmasi tak mencetak catatan) — lihat `PODetailPriceLine`.
      addOrderItemPriceLine(reseller.priceLines, priceLineIndex, cid, item, qty);
    }
  }

  const variants: PODetailVariant[] = [...variantMap.values()]
    .sort(
      (a, b) =>
        a.sortOrder - b.sortOrder ||
        smartVariantNameCompare(a.variantName, b.variantName)
    )
    .map((v) => ({
      variantId: v.variantId,
      variantName: v.variantName,
      totalQty: v.totalQty,
      resellerCount: v.buyerMap.size,
      buyers: [...v.buyerMap.values()].sort((a, b) =>
        a.buyerName.localeCompare(b.buyerName)
      ),
      notes: v.notes
    }));

  const resellers = [...resellerMap.values()]
    .sort((a, b) => a.customerName.localeCompare(b.customerName))
    .map((r) => ({
      ...r,
      items: [...r.items].sort(
        (a, b) =>
          smartVariantNameCompare(a.variantName, b.variantName) ||
          // Varian sama, catatan beda → urutkan yang tanpa catatan dulu lalu
          // alfabetis, supaya urutan barisnya tidak bergantung pada urutan
          // order mana yang kebetulan terbaca lebih dulu.
          (a.note ?? "").localeCompare(b.note ?? "")
      ),
      priceLines: sortPriceLines(r.priceLines)
    }));

  return {
    productId: header.id,
    productName: header.name,
    brand: header.brand ?? "",
    status: header.status,
    deadline: header.preorder_deadline ?? null,
    closedAt: header.closed_at ?? null,
    estimatedReady: header.estimated_ready ?? null,
    totalPcs,
    totalReseller: resellerIds.size,
    totalTransaksi: opts?.withMoney ? totalTransaksi : null,
    estimasiProfit: opts?.withMoney ? estimasiProfit : null,
    variants,
    resellers,
    productDiscounts: deriveProductDiscounts(header.product_variants ?? []),
    variantPricing: (header.product_variants ?? []).map(variantPricingFromRow)
  };
}

// ─── Baris uang & stempel konfirmasi — SATU aturan untuk Detail PO dan ──────
// Detail Produksi (pesan Konfirmasi + PDF per pelanggan dibuat dari sini di
// kedua halaman, jadi angka keduanya tak boleh bisa menyimpang).

/** Kolom `order_items` pembentuk satu baris uang; kolom uang boleh absen. */
export type PriceLineSourceItem = {
  variant_id?: string | null;
  variant_name: string;
  unit_price?: number | string | null;
  /** Potret tambahan baris (mig 7900); absen = kolom tak diminta. */
  unit_price_extra?: number | string | null;
  manual_price_normal?: number | string | null;
  voucher_name?: string | null;
  promo_discount_mode?: string | null;
  promo_discount_value?: number | string | null;
  level_discount_mode?: string | null;
  level_discount_value?: number | string | null;
};

/**
 * Tambahkan satu baris `order_items` AKTIF (pemanggil sudah membuang yang
 * batal) ke baris uang seorang pembeli: digabung per varian + HARGA
 * (`priceLineKey`), catatan diabaikan. `index` dipakai bersama lintas
 * pembeli; `scope` (kunci pembeli) memisahkannya.
 */
export function addOrderItemPriceLine(
  lines: PODetailPriceLine[],
  index: Map<string, PODetailPriceLine>,
  scope: string,
  item: PriceLineSourceItem,
  qty: number
): void {
  const line: PODetailPriceLine = {
    variantId: item.variant_id ?? null,
    variantName: item.variant_name,
    qty,
    unitPrice: numOrNull(item.unit_price),
    manualPriceNormal: numOrNull(item.manual_price_normal),
    priceExtra: numOrNull(item.unit_price_extra),
    hasVoucher: Boolean((item.voucher_name ?? "").trim()),
    trace: discountTraceFromRow(item)
  };
  const key = `${scope}\u0000${priceLineKey(line)}`;
  const existing = index.get(key);
  if (existing) {
    existing.qty += qty;
  } else {
    index.set(key, line);
    lines.push(line);
  }
}

/**
 * Urutan baris uang = urutan item (grup 🔹 pesan WA mengikuti urutan ini);
 * varian sama berharga beda → yang berharga dulu, termurah dulu.
 */
export function sortPriceLines(lines: readonly PODetailPriceLine[]): PODetailPriceLine[] {
  return [...lines].sort(
    (a, b) =>
      smartVariantNameCompare(a.variantName, b.variantName) ||
      (a.unitPrice ?? Number.MAX_SAFE_INTEGER) - (b.unitPrice ?? Number.MAX_SAFE_INTEGER)
  );
}

/**
 * Stempel konfirmasi WA (mig 3580): ambil yang TERBARU lintas order milik
 * pembeli ini. Dibandingkan lewat `Date.parse`, bukan perbandingan string —
 * stempel dari dua sumber bisa beda offset zona waktu ("+00:00" vs "Z"), dan
 * urutan leksikalnya tidak lagi mencerminkan urutan waktunya.
 */
export function mergePoConfirmStamp(
  target: { confirmedAt: string | null; confirmedByName: string | null },
  order: {
    po_confirmed_at?: string | null;
    po_confirmed_profile?:
      | { full_name: string | null }
      | { full_name: string | null }[]
      | null;
  }
): void {
  const stamp = order.po_confirmed_at ?? null;
  if (!stamp) return;
  const prev = target.confirmedAt ? Date.parse(target.confirmedAt) : NaN;
  const next = Date.parse(stamp);
  if (Number.isNaN(next) || (!Number.isNaN(prev) && next <= prev)) return;
  const rel = Array.isArray(order.po_confirmed_profile)
    ? order.po_confirmed_profile[0]
    : order.po_confirmed_profile;
  target.confirmedAt = stamp;
  target.confirmedByName = rel?.full_name ?? null;
}

/**
 * Kunci penggabungan baris uang: varian (id + nama) dan SEMUA yang membentuk
 * harganya — snapshot, harga baris, voucher, jejak potongan. Catatan sengaja
 * tak ikut (lihat `PODetailPriceLine`).
 */
function priceLineKey(line: PODetailPriceLine): string {
  return JSON.stringify([
    line.variantId,
    line.variantName,
    line.unitPrice,
    line.manualPriceNormal,
    line.priceExtra ?? 0,
    line.hasVoucher,
    line.trace
  ]);
}

/** Satu baris varian di dalam grup Variasi 1 (axis pertama nama varian). */
export type POVariantRow = {
  variant: PODetailVariant;
  /**
   * Label sisa axis (axis ke-2 dst), mis. "S" atau "S / Katun". Kosong ("")
   * bila varian hanya satu-axis (nama tanpa pemisah " / ") — dipakai untuk
   * fallback ke nama penuh saat render.
   */
  subLabel: string;
};

/** Grup rekap varian berdasar nilai Variasi 1 (axis pertama nama varian). */
export type POVariantGroup = {
  /**
   * Nilai axis pertama, mis. "Merah". Kosong ("") menandai MODE FLAT (produk
   * satu-axis): UI render daftar datar tanpa header grup dan memakai
   * `row.subLabel` (= nama varian penuh) sebagai label tiap baris.
   */
  axisLabel: string;
  /** Σ qty seluruh baris dalam grup. */
  totalQty: number;
  rows: POVariantRow[];
};

/**
 * Kelompokkan rekap per-varian berdasar **Variasi 1** (axis pertama nama varian;
 * nama dipisah " / " — lihat `buildComboLabel` di ProductForm) untuk tampilan
 * terkelompok di kartu Detail PO.
 *
 * - **Multi-axis** (ada varian bernama "A / B"): satu grup per nilai axis-1; tiap
 *   baris menampilkan sisa axis ("B", "B / C", …) lewat `subLabel`.
 * - **Satu-axis** (tak ada " / " pada semua nama): kembalikan SATU grup ber-
 *   `axisLabel` "" (mode flat) berisi semua varian; `subLabel` = nama penuh.
 *
 * Urutan grup mengikuti kemunculan pertama axis-1 pada `variants` (yang sudah
 * terurut `sort_order` dari `aggregatePODetail`); urutan baris = urutan input.
 */
export function groupPODetailVariants(
  variants: PODetailVariant[]
): POVariantGroup[] {
  if (variants.length === 0) return [];

  const multiAxis = variants.some((v) => v.variantName.includes(" / "));
  if (!multiAxis) {
    return [
      {
        axisLabel: "",
        totalQty: variants.reduce((sum, v) => sum + v.totalQty, 0),
        rows: variants.map((v) => ({ variant: v, subLabel: v.variantName }))
      }
    ];
  }

  const order: string[] = [];
  const rowsByAxis = new Map<string, POVariantRow[]>();
  for (const v of variants) {
    const parts = v.variantName
      .split(" / ")
      .map((p) => p.trim())
      .filter(Boolean);
    const axis = parts[0] ?? v.variantName;
    const subLabel = parts.slice(1).join(" / ");
    if (!rowsByAxis.has(axis)) {
      rowsByAxis.set(axis, []);
      order.push(axis);
    }
    rowsByAxis.get(axis)!.push({ variant: v, subLabel });
  }

  return order.map((axis) => {
    const rows = rowsByAxis.get(axis)!;
    return {
      axisLabel: axis,
      totalQty: rows.reduce((sum, r) => sum + r.variant.totalQty, 0),
      rows
    };
  });
}

/**
 * Kalimat penanda "sudah dikonfirmasi" pada kartu Rekap Per Pelanggan
 * (mig 3580) — mis. "Dikonfirmasi 2 jam lalu · Azra".
 *
 * Umurnya RELATIF (dipakai-ulang `formatNotifAge`) karena yang ditanyakan
 * admin di tengah menyisir daftar adalah "barusan atau kemarin?", bukan jam
 * persisnya; formatter itu sendiri sudah beralih ke tanggal begitu lewat
 * seminggu. Nama penanda ikut disebut supaya di toko ber-banyak admin jelas
 * siapa yang sudah menghubungi — tapi ia OPSIONAL: profil yang dihapus
 * membuat `confirmedByName` null (FK SET NULL), dan itu tak boleh menghapus
 * fakta bahwa konfirmasinya pernah terjadi.
 */
export function poConfirmedLabel(
  confirmedAt: string | null,
  confirmedByName?: string | null,
  now: Date = new Date()
): string | null {
  if (!confirmedAt) return null;
  if (Number.isNaN(Date.parse(confirmedAt))) return null;
  const who = (confirmedByName ?? "").trim();
  const age = formatNotifAge(confirmedAt, now);
  return who ? `Dikonfirmasi ${age} · ${who}` : `Dikonfirmasi ${age}`;
}

/**
 * Pesanan pembeli BERUBAH sesudah dikonfirmasi via WhatsApp (pemilik
 * 2026-10-06, opsi B)? Benar bila ada baris pesanan aktif yang lahir SETELAH
 * stempel konfirmasi (mig 3580). Kartu Rekap Per Pelanggan lalu kembali
 * "belum dikonfirmasi" supaya admin yang menyisir daftar mengirim ulang rekap —
 * sejak Konfirmasi juga ada di OPEN PO, pembeli masih bisa mengubah pesanannya
 * setelah dikonfirmasi, dan stempelnya tak pernah direset DB.
 *
 * Kenapa `created_at` baris: `order_items` tak punya `updated_at`. Yang
 * menulis BARIS BARU — jadi tertangkap — edit pembeli (OPEN PO) & Edit Pesanan
 * admin (hapus + sisip ulang), checkout ulang (digabung = sisip baris), Tambah
 * Produk admin. Yang TIDAK tertangkap: perubahan qty di baris yang SAMA (Ubah
 * Pcs, batal sebagian oleh admin) — dikerjakan admin sendiri. Pecah-baris
 * Submit ke Produksi / batal sebagian MEWARISI `created_at` sumbernya, jadi tak
 * memicu alarm palsu.
 *
 * Kedua cap waktu dari jam DB (`now()`), jadi sebanding. Stempel OPTIMIS layar
 * (jam perangkat) jangan diumpankan ke sini — lihat pemanggilnya.
 */
export function poConfirmChangedSince(
  confirmedAt: string | null,
  lastLineAt: string | null | undefined
): boolean {
  if (!confirmedAt || !lastLineAt) return false;
  const confirmed = Date.parse(confirmedAt);
  const line = Date.parse(lastLineAt);
  if (Number.isNaN(confirmed) || Number.isNaN(line)) return false;
  return line > confirmed;
}
